/**
 * Where the page finds the files it ships in public/ — the ONE seam for every runtime URL the app builds
 * itself (the build's own assets are vite's business).
 *
 * Every target but one serves this build from a real http origin at its root, so `/pcm-socket-worker.js`
 * is simply that. The PACKAGED build (VITE_PACKAGED=1, the Samsung Tizen app — see vite.config.ts) runs
 * from the app package over file://: a root-absolute path there is the filesystem root, and a Worker or
 * an AudioWorklet module cannot be loaded by URL from that opaque origin at all. So:
 *  • publicUrl() resolves against the build's base (`/` normally, `./` = beside the page when packaged),
 *  • scriptUrl() hands back, when packaged, a blob: URL of the script's text embedded in the bundle at
 *    build time (embeddedScripts.ts — that import is dead code, and dropped, in every other build).
 */

/** A public/ file's URL for this build: `/x` normally, `./x` (beside the page) in the packaged build. */
export function publicUrl(path: string): string {
  return `${import.meta.env.BASE_URL}${path.replace(/^\/+/, '')}`
}

const blobs = new Map<string, string>()

/** A URL a Worker / audioWorklet.addModule can load this public/ script from, on any origin. */
export async function scriptUrl(path: string): Promise<string> {
  if (import.meta.env.VITE_PACKAGED !== '1') return publicUrl(path)
  const hit = blobs.get(path)
  if (hit) return hit
  const url = URL.createObjectURL(new Blob([await scriptSource(path)], { type: 'text/javascript' }))
  blobs.set(path, url)
  return url
}

/** A public/ script's source text (the ScriptProcessor fallback evaluates a worklet's text in-page). */
export async function scriptSource(path: string): Promise<string> {
  if (import.meta.env.VITE_PACKAGED === '1') {
    const { EMBEDDED_SCRIPTS } = await import('./embeddedScripts')
    const src = EMBEDDED_SCRIPTS[path]
    if (src == null) throw new Error(`script ${path} is not embedded in the packaged build (embeddedScripts.ts)`)
    return src
  }
  const res = await fetch(publicUrl(path))
  if (!res.ok) throw new Error(`${path}: HTTP ${res.status}`)
  return res.text()
}
